import { useState, useCallback, useRef, useEffect } from 'react'

interface UseCameraOptions {
  facingMode?: 'user' | 'environment'
  width?: number
  height?: number
}

interface UseCameraReturn {
  // State
  isActive: boolean
  isLoading: boolean
  hasPermission: boolean | null
  permissionState: PermissionState | 'unknown'
  error: string | null
  stream: MediaStream | null
  capturedImage: string | null
  
  // Refs
  videoRef: React.RefObject<HTMLVideoElement>
  canvasRef: React.RefObject<HTMLCanvasElement>
  
  // Actions
  requestPermission: () => Promise<boolean>
  startCamera: () => Promise<void>
  stopCamera: () => void
  capturePhoto: () => Promise<Blob | null>
  switchCamera: () => Promise<void>
  retake: () => void
  
  // Utils
  getCapabilities: () => MediaTrackCapabilities | null
  setZoom: (level: number) => void
}

export function useCamera(options: UseCameraOptions = {}): UseCameraReturn {
  const { facingMode = 'environment', width = 1920, height = 1080 } = options
  
  const [isActive, setIsActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [permissionState, setPermissionState] = useState<PermissionState | 'unknown'>('unknown')
  const [error, setError] = useState<string | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [currentFacing, setCurrentFacing] = useState(facingMode)
  
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  // Check permission state
  const checkPermission = useCallback(async (): Promise<PermissionState | 'unknown'> => {
    try {
      // Check if Permissions API is available
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'camera' as PermissionName })
        setPermissionState(result.state)
        
        // Listen for permission changes
        result.onchange = () => {
          setPermissionState(result.state)
          if (result.state === 'denied') {
            setHasPermission(false)
          }
        }
        
        return result.state
      }
      return 'unknown'
    } catch {
      // Permissions API not supported (iOS Safari)
      return 'unknown'
    }
  }, [])

  // Check permission on mount
  useEffect(() => {
    checkPermission()
  }, [checkPermission])

  // Cleanup function
  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop()
      })
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setStream(null)
    setIsActive(false)
  }, [])

  // Stop camera on unmount
  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  // Request permission explicitly
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Check if camera API is available
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera non supportata su questo browser')
      }
      
      // Request camera access - this will trigger the permission prompt
      const testStream = await navigator.mediaDevices.getUserMedia({ 
        video: true,
        audio: false 
      })
      
      // Immediately stop the test stream
      testStream.getTracks().forEach(track => track.stop())
      
      setHasPermission(true)
      setPermissionState('granted')
      return true
      
    } catch (err) {
      console.error('Permission request error:', err)
      
      const error = err as Error
      const errorName = (err as DOMException).name || ''
      
      if (errorName === 'NotAllowedError' || error.message?.includes('Permission denied')) {
        setError('Permesso fotocamera negato. Per utilizzare questa funzione, abilita la fotocamera nelle impostazioni del browser.')
        setHasPermission(false)
        setPermissionState('denied')
      } else if (errorName === 'NotFoundError') {
        setError('Nessuna fotocamera trovata sul dispositivo.')
        setHasPermission(false)
      } else if (errorName === 'NotReadableError') {
        setError('Fotocamera in uso da un\'altra applicazione. Chiudi le altre app e riprova.')
        setHasPermission(false)
      } else if (errorName === 'OverconstrainedError') {
        setError('Le impostazioni della fotocamera richieste non sono supportate.')
        setHasPermission(false)
      } else if (errorName === 'SecurityError') {
        setError('L\'accesso alla fotocamera richiede una connessione sicura (HTTPS).')
        setHasPermission(false)
      } else {
        setError(`Errore fotocamera: ${error.message || 'Errore sconosciuto'}`)
        setHasPermission(false)
      }
      
      return false
      
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      setCapturedImage(null)
      
      // Check if camera API is available
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera non supportata su questo browser')
      }
      
      // Stop existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
      
      // Build constraints with fallbacks for mobile
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: currentFacing },
          width: { ideal: width, max: 1920 },
          height: { ideal: height, max: 1080 }
        },
        audio: false
      }
      
      console.log('Requesting camera with constraints:', constraints)
      
      let mediaStream: MediaStream
      
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
      } catch (constraintError) {
        // Fallback: try with basic constraints if the ideal ones fail
        console.warn('Fallback to basic constraints:', constraintError)
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: currentFacing },
          audio: false
        })
      }
      
      streamRef.current = mediaStream
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
        
        // Wait for video to be ready
        await new Promise<void>((resolve, reject) => {
          const video = videoRef.current!
          
          const handleLoadedMetadata = () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata)
            video.removeEventListener('error', handleError)
            resolve()
          }
          
          const handleError = () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata)
            video.removeEventListener('error', handleError)
            reject(new Error('Errore caricamento video'))
          }
          
          video.addEventListener('loadedmetadata', handleLoadedMetadata)
          video.addEventListener('error', handleError)
          
          // Timeout fallback
          setTimeout(() => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata)
            video.removeEventListener('error', handleError)
            // Resolve anyway if video seems to be playing
            if (video.readyState >= 2) {
              resolve()
            } else {
              reject(new Error('Timeout caricamento video'))
            }
          }, 5000)
        })
        
        // Play video
        try {
          await videoRef.current.play()
        } catch (playError) {
          console.warn('Autoplay failed, user interaction may be needed:', playError)
          // On some browsers, play() might fail without user interaction
          // The video should still work when the user taps the capture button
        }
      }
      
      setStream(mediaStream)
      setHasPermission(true)
      setPermissionState('granted')
      setIsActive(true)
      
      console.log('Camera started successfully')
      
    } catch (err) {
      console.error('Camera start error:', err)
      
      const error = err as Error
      const errorName = (err as DOMException).name || ''
      
      if (errorName === 'NotAllowedError' || error.message?.includes('Permission denied')) {
        setError('Permesso fotocamera negato. Abilita la fotocamera nelle impostazioni del browser e ricarica la pagina.')
        setHasPermission(false)
        setPermissionState('denied')
      } else if (errorName === 'NotFoundError') {
        setError('Nessuna fotocamera trovata sul dispositivo.')
      } else if (errorName === 'NotReadableError') {
        setError('Fotocamera in uso da un\'altra applicazione. Chiudi le altre app e riprova.')
      } else if (errorName === 'OverconstrainedError') {
        setError('Impostazioni fotocamera non supportate. Riprova.')
      } else if (errorName === 'AbortError') {
        setError('Inizializzazione fotocamera interrotta. Riprova.')
      } else if (errorName === 'SecurityError') {
        setError('Connessione sicura (HTTPS) richiesta per la fotocamera.')
      } else {
        setError(error.message || 'Errore durante l\'avvio della fotocamera')
      }
      
      setIsActive(false)
      
    } finally {
      setIsLoading(false)
    }
  }, [currentFacing, width, height])

  // Stop camera
  const stopCamera = useCallback(() => {
    cleanup()
    setCapturedImage(null)
  }, [cleanup])

  // Capture photo
  const capturePhoto = useCallback(async (): Promise<Blob | null> => {
    if (!videoRef.current || !canvasRef.current || !isActive) {
      console.error('Cannot capture: video or canvas not ready')
      return null
    }
    
    try {
      const video = videoRef.current
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        throw new Error('Canvas context non disponibile')
      }
      
      // Ensure video has valid dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        throw new Error('Video non pronto per la cattura')
      }
      
      // Set canvas size to video dimensions
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      
      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      
      // Get image as data URL for preview
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
      setCapturedImage(dataUrl)
      
      // Stop the camera stream after capture to save resources
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      
      // Convert to blob
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            console.log('Photo captured, size:', blob?.size)
            resolve(blob)
          },
          'image/jpeg',
          0.92
        )
      })
      
    } catch (err) {
      console.error('Capture error:', err)
      setError('Errore durante lo scatto della foto. Riprova.')
      return null
    }
  }, [isActive])

  // Switch camera (front/back)
  const switchCamera = useCallback(async () => {
    const newFacing = currentFacing === 'environment' ? 'user' : 'environment'
    setCurrentFacing(newFacing)
    
    // Restart camera with new facing mode
    if (isActive) {
      cleanup()
      // Small delay to ensure cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 200))
      await startCamera()
    }
  }, [currentFacing, isActive, cleanup, startCamera])

  // Retake - clear captured image and resume camera
  const retake = useCallback(async () => {
    setCapturedImage(null)
    setError(null)
    await startCamera()
  }, [startCamera])

  // Get camera capabilities
  const getCapabilities = useCallback((): MediaTrackCapabilities | null => {
    if (!streamRef.current) return null
    
    const videoTrack = streamRef.current.getVideoTracks()[0]
    if (!videoTrack) return null
    
    try {
      return videoTrack.getCapabilities()
    } catch {
      return null
    }
  }, [])

  // Set zoom level
  const setZoom = useCallback((level: number) => {
    if (!streamRef.current) return
    
    const videoTrack = streamRef.current.getVideoTracks()[0]
    if (!videoTrack) return
    
    try {
      const capabilities = videoTrack.getCapabilities()
      
      // Check if zoom is supported
      if ('zoom' in capabilities) {
        const { min = 1, max = 1 } = capabilities.zoom as { min: number; max: number }
        const clampedLevel = Math.max(min, Math.min(max, level))
        
        videoTrack.applyConstraints({
          // @ts-expect-error - zoom is not in standard types
          advanced: [{ zoom: clampedLevel }]
        }).catch(console.error)
      }
    } catch {
      // Zoom not supported
    }
  }, [])

  return {
    // State
    isActive,
    isLoading,
    hasPermission,
    permissionState,
    error,
    stream,
    capturedImage,
    
    // Refs
    videoRef,
    canvasRef,
    
    // Actions
    requestPermission,
    startCamera,
    stopCamera,
    capturePhoto,
    switchCamera,
    retake,
    
    // Utils
    getCapabilities,
    setZoom
  }
}
